import { getSiteBaseUrl } from '@/lib/siteBaseUrl'
import {
	MercariProductTypeEnum,
	type TypeMercariProductStatus,
} from '@/types/Mercari'
import { isStagingEnvironment, checkIsSeoEnhancement } from '@/lib/checkService'

type TMercariPage = 'index' | 'search' | 'detail' | 'campaign'
type TQueryParams = Record<string, string | number | boolean>
type TMercariDiscountPercentage = number | null | string
interface IMercariPayload {
	id?: string
	shop_id?: string
	shop_name?: string
	searchQuery?: string
	queryParams?: TQueryParams
	slug?: string
	category_id?: string
}

function isCurrentQueryIncludeProd() {
	return new URLSearchParams(window.location.search).get('_prod') === 'true'
}

export function isEnableProdQueryFlexibility() {
	return !(import.meta.env.PROD && !isStagingEnvironment())
}

function shouldAddProdQueryToRedirectUrl() {
	return isEnableProdQueryFlexibility() && isCurrentQueryIncludeProd()
}

export function shouldUseMercariProdApi() {
	return !isEnableProdQueryFlexibility() || isCurrentQueryIncludeProd()
}

function getPath(type: TMercariPage, payload?: IMercariPayload) {
	const isProd = import.meta.env.PROD
	const baseUrl = isProd ? getSiteBaseUrl() : ''
	const isSeoEnhancement = checkIsSeoEnhancement()
	const indexPathProduction = isSeoEnhancement
		? '/shopping/marketplaces/mercari/'
		: '/mercari/'
	const searchPathProduction = isSeoEnhancement
		? '/shopping/marketplaces/mercari/search/'
		: '/mercari/search/'
	const detailPathProduction = isSeoEnhancement
		? `/shopping/marketplaces/mercari/products/${payload?.id}/`
		: `/mercari/products/${payload?.id}/`

	const pathMap: Record<TMercariPage, string> = {
		index: isProd ? indexPathProduction : '/mercari-index',
		search: isProd ? searchPathProduction : '/mercari-search',
		detail: isProd ? detailPathProduction : '/mercari-detail',
		campaign: isProd
			? `/campaign/${payload?.slug}/`
			: `/mercari-campaign?slug=${payload?.slug}`,
	}

	if (type === 'detail' && !payload?.id) {
		throw new Error('Detail page requires an ID.')
	}

	return `${isProd ? baseUrl : ''}${pathMap[type]}`
}

function getQueryParams(type?: TMercariPage, payload?: IMercariPayload) {
	const params = new URLSearchParams()

	if (!import.meta.env.PROD) {
		if (type === 'detail' && payload?.id) {
			params.set('id', payload.id)
		}
	}

	if (type === 'search' && payload) {
		const {
			searchQuery,
			category_id: categoryId,
			shop_id: shopId,
			shop_name: shopName,
		} = payload

		if (searchQuery) params.set('q', searchQuery)
		if (categoryId) params.set('category_id', categoryId)

		if (shopId) {
			params.set('shop_id', shopId)
			if (shopName) params.set('shop_name', shopName)
		}
	}

	if (shouldAddProdQueryToRedirectUrl()) {
		params.set('_prod', 'true')
	} else {
		params.delete('_prod')
	}

	if (payload?.queryParams) {
		Object.entries(payload.queryParams).forEach(([key, value]) => {
			params.set(key, String(value))
		})
	}

	return params.toString() ? `?${params.toString()}` : ''
}

/**
 * Constructs a Mercari URL based on the provided page type and optional payload.
 *
 * @param type - index | search | detail
 * @param payload - id | searchQuery | queryParams => dynamic query object
 * @overload - Also supports MercariSearchParams type for search pages (新增支持MercariSearchParams类型)
 * @example getMercariUrl('index') => 'https://test-tw.buynship.com/mercari/'
 * Local => '/mercari-index'
 * @example getMercariUrl('search', { searchQuery: 'iphone' }) => 'https://test-tw.buynship.com/mercari/search/?q=iphone'
 * Local => '/mercari-search?q=iphone'
 * @example getMercariUrl('detail', { id: '123' }) => 'https://test-tw.buynship.com/mercari/products/123/'
 * Local => '/mercari-detail?id=123'
 * @example getMercariUrl('search', { searchQuery: 'iphone', queryParams: { page: 2, sort: 'price' } }) => 'https://test-tw.buynship.com/mercari/search/iphone?page=2&sort=price'
 * Local => '/mercari-search?q=iphone&page=2&sort=price'
 * @example getMercariUrl('campaign', { slug: 'campaign-slug' }) => 'https://test-tw.buynship.com/mercari/campaign-slug'
 * Local => '/mercari-campaign?slug=campaign-slug'
 */
export function getMercariUrl(type: TMercariPage, payload?: IMercariPayload) {
	return `${getPath(type, payload)}${getQueryParams(type, payload)}`
}

/**
 * Formats a given price with an optional currency symbol.
 *
 * @param price - The price to be formatted.
 * @param currency - The optional currency code. Defaults to '$' if not provided.
 *                    If 'JPY' is provided, the currency symbol will be 'JP¥'.
 * @returns The formatted price string with the appropriate currency symbol.
 * @example getFormattedPrice(1000) => '$ 1,000'
 * @example getFormattedPrice(1000, 'JPY') => 'JP¥ 1,000'
 */
export function getFormattedPrice(price: number, currency?: string) {
	const currencySymbol = currency === 'JPY' ? 'JP¥' : '$'
	return `${currencySymbol} ${price.toLocaleString('en')}`
}

export function getRegularMercariStatus({
	status,
	discountPercentage,
	hasPromotions,
}: {
	status: TypeMercariProductStatus
	discountPercentage: TMercariDiscountPercentage
	hasPromotions: boolean
}): MercariProductTypeEnum {
	const isSoldOut =
		status === MercariProductTypeEnum.SOLD_OUT ||
		status === MercariProductTypeEnum.TRADING

	const hasDiscount =
		discountPercentage !== null &&
		typeof discountPercentage === 'number' &&
		discountPercentage > 0

	if (isSoldOut) return MercariProductTypeEnum.SOLD_OUT
	if (hasDiscount) return MercariProductTypeEnum.DISCOUNT
	if (hasPromotions) return MercariProductTypeEnum.SALE

	return MercariProductTypeEnum.NORMAL
}

export function getBuyaholicMercariStatus({
	discountPercentage,
}: {
	discountPercentage: TMercariDiscountPercentage
}): MercariProductTypeEnum {
	switch (discountPercentage) {
		case 'discount_not_available':
			return MercariProductTypeEnum.DISCOUNT_NOT_AVAILABLE
		case '':
			return MercariProductTypeEnum.NORMAL
		default:
			return MercariProductTypeEnum.SAVED_PERCENTAGE
	}
}

export function getMercariStatus({
	isBuyaholicProduct,
	status,
	discountPercentage,
	hasPromotions,
}: {
	isBuyaholicProduct: boolean
	status: TypeMercariProductStatus
	discountPercentage: TMercariDiscountPercentage
	hasPromotions: boolean
}): MercariProductTypeEnum {
	if (isBuyaholicProduct) {
		return getBuyaholicMercariStatus({ discountPercentage })
	}
	return getRegularMercariStatus({
		status,
		discountPercentage,
		hasPromotions,
	})
}

export function getKeywordTabUrl(
	value: string,
	keywordTabs: { shopId: string; label: string; value: string }[],
) {
	const selectedTab = keywordTabs?.find(tab => tab.value === value)

	const queryParams = selectedTab?.shopId
		? {
				shop_id: selectedTab.shopId,
				shop_name: selectedTab.label,
		  }
		: { searchQuery: value }

	return getMercariUrl('search', queryParams)
}

export function replaceBrand<T>(input: T, brandValue: string): T {
	if (typeof input === 'string') {
		return input.replaceAll('{brand}', brandValue) as unknown as T
	}
	if (Array.isArray(input)) {
		return input.map(item => replaceBrand(item, brandValue)) as unknown as T
	}
	if (input && typeof input === 'object') {
		const result = Object.entries(input).reduce(
			(acc, [key, value]) => {
				acc[key] = replaceBrand(value, brandValue)
				return acc
			},
			{} as Record<string, any>,
		)
		return result as T
	}
	return input
}
